import React, { createContext, useCallback, useMemo, useState } from 'react';

export type PlaygroundView =
  | 'main'
  | 'direct'
  | 'agent'
  | 'definitions';

export type PlaygroundError = {
  id: string;
  title: string;
  description?: string;
  details?: string;
  timestamp: number;
};

export type PlaygroundContextType = {
  currentView: PlaygroundView;
  setCurrentView: (view: PlaygroundView) => void;
  errors: PlaygroundError[];
  pushError: (
    error: Omit<PlaygroundError, 'id' | 'timestamp'> & { id?: string }
  ) => void;
  dismissError: (id?: string) => void;
  clearErrors: () => void;
};

export const PlaygroundContext = createContext<PlaygroundContextType>({
  currentView: 'main',
  setCurrentView: () => undefined,
  errors: [],
  pushError: () => undefined,
  dismissError: () => undefined,
  clearErrors: () => undefined
});

export const PlaygroundProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [currentView, setCurrentView] = useState<PlaygroundView>('main');
  const [errors, setErrors] = useState<PlaygroundError[]>([]);

  const pushError = useCallback(
    (error: Omit<PlaygroundError, 'id' | 'timestamp'> & { id?: string }) => {
      const id =
        error.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setErrors((prev) => [
        {
          id,
          title: error.title,
          description: error.description,
          details: error.details,
          timestamp: Date.now()
        },
        ...prev
      ]);
    },
    []
  );

  const dismissError = useCallback((id?: string) => {
    setErrors((prev) => (id ? prev.filter((e) => e.id !== id) : prev.slice(1)));
  }, []);

  const clearErrors = useCallback(() => setErrors([]), []);

  const value = useMemo<PlaygroundContextType>(
    () => ({
      currentView,
      setCurrentView,
      errors,
      pushError,
      dismissError,
      clearErrors
    }),
    [currentView, errors, pushError, dismissError, clearErrors]
  );

  return (
    <PlaygroundContext.Provider value={value}>
      {children}
    </PlaygroundContext.Provider>
  );
};
